import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BenchmarkComparisonRow,
  CrossCampaignPattern,
  IntelligencePayload,
  getIntelligence,
} from '../../../services/cgBoostApi';
import { ViewDataBoundary } from '../useViewData';
import { cardStyle, eyebrowLabel, flagToStatus, mutedText, sectionTitle, tinyTable, tinyTd, tinyTh } from '../styles';
import { StatusPill } from '../../ui';

interface Props { campaignId: string; }

const METRIC_LABEL: Record<BenchmarkComparisonRow['metric'], { social: string; paid: string; format: 'percent' | 'currency' }> = {
  cpm:      { social: 'Distribution Efficiency', paid: 'CPM',       format: 'currency' },
  vtr:      { social: 'Audience Attention',      paid: 'VTR',       format: 'percent' },
  hookRate: { social: 'Hook Strength',            paid: 'Hook Rate', format: 'percent' },
  ctr:      { social: 'Audience Action',          paid: 'CTR',       format: 'percent' },
};

const IntelligenceView: React.FC<Props> = ({ campaignId }) => (
  <ViewDataBoundary
    loader={getIntelligence}
    campaignId={campaignId}
    loadingLabel="Loading intelligence…"
    render={(data) => <IntelligenceBody data={data} />}
  />
);

const IntelligenceBody: React.FC<{ data: IntelligencePayload }> = ({ data }) => {
  const navigate = useNavigate();
  return (
    <>
      <div style={cardStyle}>
        <div style={sectionTitle}>Benchmark Comparison — current vs CG Boost historical average</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {data.benchmarkComparison.map((row) => (
            <BenchmarkCard key={row.metric} row={row} />
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        <PatternCard title="Top Performing Formats" subtitle="By avg Resonance" rows={data.topFormats} />
        <PatternCard title="Top Performing Creators" subtitle="By avg Resonance" rows={data.topCreators} />
        <PatternCard title="Top Performing Hook Styles" subtitle="By avg CTR" rows={data.topHookStyles} valueFormat="percent" />
      </div>

      <div style={cardStyle}>
        <div style={sectionTitle}>Brief Intelligence</div>
        <div style={{
          padding: 14, borderRadius: 10,
          background: 'rgba(79,70,229,0.06)', border: '1px solid rgba(79,70,229,0.25)',
          fontSize: 14, color: 'var(--fg-1)',
        }}>
          {data.briefRecommendation}
        </div>
        <button
          onClick={() => navigate('/chat')}
          style={{
            marginTop: 12, padding: '10px 14px', borderRadius: 8, border: 'none',
            background: 'var(--accent)', color: 'white',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Generate brief recommendations in chat →
        </button>
        <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 6 }}>
          Opens the Culture Genius chat. Once the Campaign Intelligence Agent tool ships
          (Phase 8), this prefills a query against historical CG Boost data.
        </div>
      </div>
    </>
  );
};

const BenchmarkCard: React.FC<{ row: BenchmarkComparisonRow }> = ({ row }) => {
  const meta = METRIC_LABEL[row.metric];
  const fmt = (v: number) => meta.format === 'percent' ? `${(v * 100).toFixed(2)}%` : `$${v.toFixed(2)}`;
  const variant = row.direction === 'better' ? 'top' : row.direction === 'worse' ? 'warn' : 'muted';
  const deltaPctText = `${row.deltaPct >= 0 ? '+' : ''}${(row.deltaPct * 100).toFixed(1)}%`;

  return (
    <div style={{
      padding: 14, borderRadius: 10,
      border: '1px solid var(--border-1)', background: 'var(--bg-app)',
    }}>
      <div style={eyebrowLabel}>{meta.social}</div>
      <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>{meta.paid}</div>
      <div style={{ marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 22, fontWeight: 700 }}>{fmt(row.current)}</span>
        <StatusPill variant={flagToStatus[variant]} noDot size="sm">{deltaPctText} vs avg</StatusPill>
      </div>
      <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>
        Historical avg: {fmt(row.historicalAvg)}
      </div>
    </div>
  );
};

const PatternCard: React.FC<{
  title: string;
  subtitle: string;
  rows: CrossCampaignPattern[];
  valueFormat?: 'percent' | 'decimal';
}> = ({ title, subtitle, rows, valueFormat = 'decimal' }) => (
  <div style={cardStyle}>
    <div>
      <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>
      <div style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 10 }}>{subtitle}</div>
    </div>
    {rows.length === 0 ? (
      <div style={mutedText}>No data yet.</div>
    ) : (
      <table style={tinyTable}>
        <thead>
          <tr>
            <th style={tinyTh}>Label</th>
            <th style={{ ...tinyTh, textAlign: 'right' }}>Appearances</th>
            <th style={{ ...tinyTh, textAlign: 'right' }}>Avg</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td style={{ ...tinyTd, fontWeight: 500 }}>{r.label}</td>
              <td style={{ ...tinyTd, textAlign: 'right' }}>{r.appearances}</td>
              <td style={{ ...tinyTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {valueFormat === 'percent' ? `${(r.averageScore * 100).toFixed(2)}%` : (r.averageScore * 100).toFixed(1) + '%'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default IntelligenceView;
